import _toConsumableArray from "@babel/runtime/helpers/esm/toConsumableArray";
import { createStore, applyMiddleware } from 'redux';
import { combineReducers } from 'redux-immutable';
import flowRight from 'lodash/flowRight';

// middleware
import createLogger from 'redux-logger';
import multi from 'redux-multi';
import thunkMiddleware from 'redux-thunk';
import alertTimerMiddleware from './common/middleware/alertTimerMiddleware';
import crossDomainMessagingMiddleware from './common/middleware/crossDomainMessagingMiddleware';
import eventLoggerMiddlewareCreator from './common/middleware/eventLoggerMiddleware';
import { createQuizTimerWebSocketMiddleware } from './common/middleware/quizTimerWebSocketMiddleware';
import { createSharedBanksMiddleware } from './common/middleware/sharedBanksMiddleware';

// reducers
import alerts from './common/reducers/alerts';
import banks from './common/reducers/banks';
import bankEntries from './common/reducers/bankEntries';
import calls from './common/reducers/calls';
import configReducer from './common/reducers/config';
import editing from './common/reducers/editing';
import errorsShowing from './common/reducers/errorsShowing';
import quizExport from './common/reducers/quizExport';
import features from './common/reducers/features';
import grading from './common/reducers/grading';
import interactionTypes from './common/reducers/interactionTypes';
import items from './common/reducers/items';
import modal from './common/reducers/modal';
import moderation from './common/reducers/moderation';
import modifications from './common/reducers/modifications';
import outcomes from './common/reducers/outcomes';
import quizzes from './common/reducers/quizzes';
import quizEntries from './common/reducers/quizEntries';
import quizSessions from './common/reducers/quizSessions';
import quizSessionEvents from './common/reducers/quizSessionEvents';
import quizSessionResults from './common/reducers/quizSessionResults';
import regrading from './common/reducers/regrading';
import reporting from './common/reducers/reporting';
import scrolling from './common/reducers/scrolling';
import bankSearch from './common/reducers/bankSearch';
import sessionItems from './common/reducers/sessionItems';
import sessionItemResults from './common/reducers/sessionItemResults';
import tagAssociations from './common/reducers/tagAssociations';
import sidebar from './common/reducers/sidebar';
import stimuli from './common/reducers/stimuli';
import taking from './common/reducers/taking';
import ui from './common/reducers/ui';
import upload from './common/reducers/upload';
import qtiImports from './common/reducers/qtiImports';
import printing from './common/reducers/printing';
import rce from './common/reducers/rce';
import analyses from './common/reducers/analyses';
import sharingModal from './common/reducers/sharingModal';
import dragAndDrop from './common/reducers/dragAndDrop';
import sharedBanks from './common/reducers/sharedBanks';
export function configureStore(_ref) {
  var _ref$extraMiddleware = _ref.extraMiddleware,
    extraMiddleware = _ref$extraMiddleware === void 0 ? [] : _ref$extraMiddleware,
    _ref$initialState = _ref.initialState,
    initialState = _ref$initialState === void 0 ? void 0 : _ref$initialState,
    _ref$inDevelopment = _ref.inDevelopment,
    inDevelopment = _ref$inDevelopment === void 0 ? false : _ref$inDevelopment,
    _ref$isTest = _ref.isTest,
    isTest = _ref$isTest === void 0 ? false : _ref$isTest,
    _ref$middlewareOverri = _ref.middlewareOverrides,
    middlewareOverrides = _ref$middlewareOverri === void 0 ? {} : _ref$middlewareOverri;
  var loggerMiddleware = createLogger({
    // immutable state returned as raw JS
    // for readability's sake
    stateTransformer: function stateTransformer(s) {
      return s.toJS();
    },
    collapsed: true,
    titleFormatter: function titleFormatter(action) {
      return "SDK action %c".concat(String(action.type));
    }
  });
  var getMiddleware = function getMiddleware(implementation, override) {
    return isTest && override ? override : implementation;
  };
  var middlewares = [getMiddleware(alertTimerMiddleware, middlewareOverrides.alertTimerMiddleware), getMiddleware(crossDomainMessagingMiddleware, middlewareOverrides.crossDomainMessagingMiddleware), getMiddleware(multi, middlewareOverrides.multiMiddleware), getMiddleware(eventLoggerMiddlewareCreator(), middlewareOverrides.kinesisMiddleware), getMiddleware(thunkMiddleware, middlewareOverrides.thunkMiddleware), getMiddleware(createQuizTimerWebSocketMiddleware(), middlewareOverrides.websocketMiddleware), getMiddleware(createSharedBanksMiddleware(), middlewareOverrides.sharedBanksMiddleware)];
  var devMiddleware = [getMiddleware(loggerMiddleware, middlewareOverrides.logger)];
  if (inDevelopment) {
    middlewares.push.apply(middlewares, devMiddleware);
  }
  if (extraMiddleware && extraMiddleware.length > 0) {
    middlewares.push.apply(middlewares, _toConsumableArray(extraMiddleware));
  }
  var createStoreWithMiddlewareApplied = flowRight(applyMiddleware.apply(void 0, middlewares),
  // todo: tweak middleware to handle (re)routing with devtools
  window.devToolsExtension ? window.devToolsExtension({
    name: 'quiz-core'
  }) : function (f) {
    return f;
  })(createStore);
  var rootReducer = combineReducers({
    dragAndDrop: dragAndDrop,
    alerts: alerts,
    banks: banks,
    bankEntries: bankEntries,
    sharedBanks: sharedBanks,
    sharingModal: sharingModal,
    calls: calls,
    config: configReducer,
    editing: editing,
    errorsShowing: errorsShowing,
    quizExport: quizExport,
    features: features,
    grading: grading,
    interactionTypes: interactionTypes,
    items: items,
    modal: modal,
    moderation: moderation,
    modifications: modifications,
    outcomes: outcomes,
    printing: printing,
    quizzes: quizzes,
    quizEntries: quizEntries,
    quizSessions: quizSessions,
    quizSessionEvents: quizSessionEvents,
    quizSessionResults: quizSessionResults,
    qtiImports: qtiImports,
    regrading: regrading,
    reporting: reporting,
    scrolling: scrolling,
    bankSearch: bankSearch,
    sessionItems: sessionItems,
    sessionItemResults: sessionItemResults,
    sidebar: sidebar,
    stimuli: stimuli,
    tagAssociations: tagAssociations,
    taking: taking,
    ui: ui,
    upload: upload,
    rce: rce,
    analyses: analyses
  });
  return createStoreWithMiddlewareApplied(rootReducer, initialState);
}
export default configureStore;